import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
  Clock, Phone, MessageCircle, MessageSquare, FileText, 
  TrendingUp, Calendar, Search, Filter, ChevronDown, ChevronRight,
  ArrowDownLeft, ArrowUpRight, AlertCircle, Megaphone, Users, Mail,
  Check, X, Target, Eye, Info, User, FolderOpen, CalendarDays,
  MapPin, ExternalLink, ArrowDown, ArrowUp, ShoppingCart,
  Download, Image as ImageIcon, FileSpreadsheet, FileVideo, FileAudio, FileArchive,
  Send
} from 'lucide-react';
import { format, formatDistanceToNow, subDays, startOfDay, endOfDay } from 'date-fns';
import { it } from 'date-fns/locale';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@easyflow/ui-frontend/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@easyflow/ui-frontend/components/ui/tabs';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Tooltip as TooltipUI, TooltipContent, TooltipProvider, TooltipTrigger } from '@easyflow/ui-frontend/components/ui/tooltip';
import { apiClient } from '@/lib/api';
import LeadTimelineModal from '@/components/crm/LeadTimelineModal';

interface LinkedLead {
  id: string;
  title: string;
  stage_name: string | null;
  stage_color: string | null;
  created_at: string;
}

interface Interaction {
  id: string;
  conversation_id?: string;
  campaign_id?: string;
  type: 'call' | 'whatsapp' | 'sms' | 'telegram' | 'form' | 'lead' | 'appointment';
  direction?: 'inbound' | 'outbound';
  content?: string;
  timestamp: string;
  status?: string;
  duration?: number;
  caller_id_number?: string;
  callee_id_number?: string;
  form_name?: string;
  stage_name?: string;
  stage_color?: string;
  title?: string;
  channel?: string;
}

interface Campaign {
  id: string;
  name: string;
  description?: string;
  type: 'sms' | 'whatsapp' | 'outbound_call' | 'email';
  status: 'draft' | 'active' | 'paused' | 'completed' | 'cancelled';
  startDate?: string;
  endDate?: string;
  createdAt: string;
  macro: {
    total: number;
    contacted: number;
    responded: number;
    converted: number;
    percentContacted: number;
    percentResponded: number;
  };
  micro: {
    status: 'pending' | 'contacted' | 'responded' | 'converted' | 'failed' | 'skipped';
    attempts: number;
    lastAttemptAt?: string;
    result?: string;
    messageCount: number;
    targetCreatedAt: string;
    lead?: {
      id: string;
      title: string;
      status: string;
      stageName: string;
      stageColor: string;
    } | null;
  };
}

interface InteractionStats {
  stats: {
    total: number;
    calls: number;
    whatsapp: number;
    telegram: number;
    sms: number;
  };
  totalCalls?: number;
  totalMessages?: number;
  lastContact: {
    channel: string;
    timestamp: string;
  } | null;
  trend: Array<{
    date: string;
    channel: string;
    count: number;
  }>;
  channelDistribution: Array<{
    channel: string;
    count: number;
    percentage: number;
  }>;
}

interface ContactDetails {
  id: string;
  first_name?: string;
  last_name?: string;
  company_name?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  address?: string;
  city?: string;
  province?: string;
  postal_code?: string;
  country?: string;
  fiscal_code?: string;
  vat_number?: string;
  notes?: string;
  tags?: string[];
  customer_type?: 'b2c' | 'b2b';
  person_id?: string;
  created_at?: string;
  updated_at?: string;
}

interface Appointment {
  id: string;
  title: string;
  description?: string;
  start_time: string;
  end_time: string;
  status: string;
  location?: string;
  meeting_link?: string;
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  notes?: string;
  service_type_name?: string;
  assigned_to_name?: string;
  store_name?: string;
}

interface EmailMessage {
  id: string;
  from_email: string;
  to_email: string;
  cc?: string;
  subject: string;
  body_text?: string;
  body_html?: string;
  direction: 'inbound' | 'outbound';
  status: string;
  created_at: string;
  read_at?: string;
}

interface SaleRecord {
  id: string;
  sale_number?: string;
  sale_date?: string;
  created_at?: string;
  total: string;
  sale_type?: string;
  operator_name?: string;
  customer_name?: string;
  items_summary?: string;
  items_count?: number;
  payment_method_name?: string;
  warehouse_name?: string;
}

type DocumentSource = 'lead' | 'customer' | 'appointment' | 'form' | 'email' | 'message';

interface PersonDocument {
  id: string;
  source_type: DocumentSource;
  source_id: string;
  source_label: string;
  source_url: string | null;
  filename: string;
  mime_type: string | null;
  size_bytes: number | null;
  created_at: string;
  uploaded_by: string | null;
  url: string | null;
}

interface PersonDocumentsResponse {
  items: PersonDocument[];
  total: number;
  limit: number;
  offset: number;
  counts_by_source?: Record<DocumentSource, number>;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: { type: 'b2c' | 'b2b' | 'person'; id: string; name: string } | null;
}

const DOCUMENTS_PAGE_SIZE = 50;

const DOCUMENT_SOURCE_LABELS: Record<DocumentSource, string> = {
  lead: 'Lead',
  customer: 'Cliente',
  appointment: 'Appuntamento',
  form: 'Form',
  email: 'Email',
  message: 'Messaggio',
};

function formatBytes(bytes: number | null): string {
  if (bytes == null || bytes <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

function getDocumentIcon(mime: string | null, filename: string): JSX.Element {
  const m = (mime || '').toLowerCase();
  const ext = (filename.split('.').pop() || '').toLowerCase();
  if (m.startsWith('image/') || ['jpg','jpeg','png','gif','webp','svg'].includes(ext)) return <ImageIcon className="w-5 h-5 text-purple-500" />;
  if (m.startsWith('video/') || ['mp4','mov','avi','webm','mkv'].includes(ext)) return <FileVideo className="w-5 h-5 text-pink-500" />;
  if (m.startsWith('audio/') || ['mp3','wav','m4a','ogg'].includes(ext)) return <FileAudio className="w-5 h-5 text-amber-500" />;
  if (m.includes('spreadsheet') || m.includes('excel') || ['xls','xlsx','csv'].includes(ext)) return <FileSpreadsheet className="w-5 h-5 text-emerald-500" />;
  if (m.includes('zip') || m.includes('compressed') || ['zip','rar','7z','tar','gz'].includes(ext)) return <FileArchive className="w-5 h-5 text-orange-500" />;
  return <FileText className="w-5 h-5 text-blue-500" />;
}

interface RenderDocumentsTabArgs {
  documents: PersonDocument[];
  documentsLoading: boolean;
  documentsError: string | null;
  documentsPersonId: string | null;
  documentsSourceFilter: DocumentSource | 'all';
  setDocumentsSourceFilter: (v: DocumentSource | 'all') => void;
  documentsSearch: string;
  setDocumentsSearch: (v: string) => void;
  documentsTotal: number;
  documentsCounts: Record<DocumentSource, number> | null;
  onLoadMore: () => void;
}

function renderDocumentsTab(args: RenderDocumentsTabArgs): JSX.Element {
  const {
    documents, documentsLoading, documentsError, documentsPersonId,
    documentsSourceFilter, setDocumentsSourceFilter,
    documentsSearch, setDocumentsSearch,
    documentsTotal, documentsCounts, onLoadMore,
  } = args;

  if (!documentsPersonId) {
    return (
      <div className="p-4 rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
        <div className="text-center text-gray-500 py-12">
          <FolderOpen className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-medium mb-2">Documenti</h3>
          <p className="text-sm">Nessuna persona collegata a questo contatto: la vista aggregata non è disponibile.</p>
        </div>
      </div>
    );
  }

  const filtered = documents;
  const totalAll =
    (documentsCounts?.lead ?? 0) + (documentsCounts?.customer ?? 0) +
    (documentsCounts?.appointment ?? 0) + (documentsCounts?.form ?? 0) +
    (documentsCounts?.email ?? 0) + (documentsCounts?.message ?? 0);
  const countsBySource: Record<DocumentSource, number> = documentsCounts ?? {
    lead: 0, customer: 0, appointment: 0, form: 0, email: 0, message: 0,
  };
  const hasMore = documents.length < documentsTotal;

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={documentsSearch}
            onChange={(e) => setDocumentsSearch(e.target.value)}
            placeholder="Cerca per nome file..."
            className="pl-9"
          />
        </div>
        <Select value={documentsSourceFilter} onValueChange={(v) => setDocumentsSourceFilter(v as DocumentSource | 'all')}>
          <SelectTrigger className="sm:w-56">
            <SelectValue placeholder="Tutte le sorgenti" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le sorgenti ({totalAll})</SelectItem>
            <SelectItem value="lead">Lead ({countsBySource.lead})</SelectItem>
            <SelectItem value="customer">Clienti ({countsBySource.customer})</SelectItem>
            <SelectItem value="message">Messaggi ({countsBySource.message})</SelectItem>
            <SelectItem value="form">Form ({countsBySource.form})</SelectItem>
            <SelectItem value="appointment">Appuntamenti ({countsBySource.appointment})</SelectItem>
            <SelectItem value="email">Email ({countsBySource.email})</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {documentsLoading && (
        <div className="p-6 rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-center text-sm text-gray-500">
          Caricamento documenti...
        </div>
      )}

      {!documentsLoading && documentsError && (
        <div className="p-4 rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          {documentsError}
        </div>
      )}

      {!documentsLoading && !documentsError && filtered.length === 0 && (
        <div className="p-4 rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <div className="text-center text-gray-500 py-12">
            <FolderOpen className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-medium mb-2">Nessun documento</h3>
            <p className="text-sm">
              {documents.length === 0
                ? 'Non ci sono allegati collegati a questa persona.'
                : 'Nessun documento corrisponde ai filtri selezionati.'}
            </p>
          </div>
        </div>
      )}

      {!documentsLoading && !documentsError && filtered.length > 0 && (
        <div className="rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-600 dark:text-gray-400 text-xs uppercase">
              <tr>
                <th className="text-left py-2 px-3">File</th>
                <th className="text-left py-2 px-3">Sorgente</th>
                <th className="text-left py-2 px-3">Dimensione</th>
                <th className="text-left py-2 px-3">Data</th>
                <th className="text-center py-2 px-3 w-20">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {filtered.map((doc) => (
                <tr key={doc.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/40">
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-2 min-w-0">
                      {getDocumentIcon(doc.mime_type, doc.filename)}
                      <div className="min-w-0">
                        <div className="font-medium truncate" title={doc.filename}>{doc.filename}</div>
                        {doc.mime_type && (
                          <div className="text-xs text-gray-500 truncate">{doc.mime_type}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex flex-col gap-0.5">
                      <Badge variant="outline" className="text-xs w-fit">{DOCUMENT_SOURCE_LABELS[doc.source_type]}</Badge>
                      {doc.source_url ? (
                        <a
                          href={doc.source_url}
                          className="text-xs text-blue-600 hover:text-blue-800 hover:underline truncate max-w-[260px]"
                          title={doc.source_label}
                        >
                          {doc.source_label}
                        </a>
                      ) : (
                        <span className="text-xs text-gray-500 truncate max-w-[260px]" title={doc.source_label}>{doc.source_label}</span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-3 text-gray-600 dark:text-gray-300">{formatBytes(doc.size_bytes)}</td>
                  <td className="py-2 px-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                    {format(new Date(doc.created_at), 'dd MMM yyyy HH:mm', { locale: it })}
                  </td>
                  <td className="py-2 px-3 text-center">
                    {doc.url ? (
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={doc.filename}
                        className="inline-flex items-center justify-center text-blue-500 hover:text-blue-700 transition-colors"
                        title="Scarica / apri"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {hasMore && (
            <div className="p-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between text-xs">
              <span className="text-gray-500">Mostrati {documents.length} di {documentsTotal}</span>
              <Button size="sm" variant="outline" onClick={onLoadMore} disabled={documentsLoading}>
                Carica altri
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const COLORS = {
  call: '#3B82F6',
  whatsapp: '#22C55E', 
  telegram: '#0088CC',
  sms: '#06B6D4',
  form: '#A855F7',
  lead: '#F97316'
};

export function InteractionHistoryModal({ open, onOpenChange, target }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [interactions, setInteractions] = useState<Interaction[]>([]);
  const [stats, setStats] = useState<InteractionStats | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignsExpanded, setCampaignsExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('anagrafica');
  const [contactDetails, setContactDetails] = useState<ContactDetails | null>(null);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [channelFilter, setChannelFilter] = useState<string>('all');
  const [campaignFilter, setCampaignFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<string>('all');
  const [expandedConversations, setExpandedConversations] = useState<Set<string>>(new Set());
  
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [appointmentsLoaded, setAppointmentsLoaded] = useState(false);
  
  const [emails, setEmails] = useState<EmailMessage[]>([]);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [emailsLoaded, setEmailsLoaded] = useState(false);
  const [expandedEmail, setExpandedEmail] = useState<string | null>(null);

  const [sales, setSales] = useState<SaleRecord[]>([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesLoaded, setSalesLoaded] = useState(false);

  // Valore con cui e' stato eseguito l'ultimo fetch: serve a rifare il
  // caricamento UNA SOLA VOLTA quando person_id/email arrivano in ritardo,
  // confrontando il cambio reale di valore invece del toggle di `loaded`.
  const salesPersonIdRef = useRef<string | null>(null);
  const emailRef = useRef<string | null>(null);

  const [documents, setDocuments] = useState<PersonDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsLoaded, setDocumentsLoaded] = useState(false);
  const [documentsError, setDocumentsError] = useState<string | null>(null);
  const [documentsSourceFilter, setDocumentsSourceFilter] = useState<DocumentSource | 'all'>('all');
  const [documentsSearch, setDocumentsSearch] = useState('');
  const [documentsTotal, setDocumentsTotal] = useState(0);
  const [documentsOffset, setDocumentsOffset] = useState(0);
  const [documentsCounts, setDocumentsCounts] = useState<Record<DocumentSource, number> | null>(null);

  const [linkedLeads, setLinkedLeads] = useState<LinkedLead[]>([]);
  const [openLeadId, setOpenLeadId] = useState<string | null>(null);
  const [openLeadName, setOpenLeadName] = useState<string>('');
  const [leadsPickerOpen, setLeadsPickerOpen] = useState(false);

  const loadData = useCallback(async () => {
    if (!target) return;
    setLoading(true);
    setError(null);
    try {
      let startDate: string | undefined;
      let endDate: string | undefined;
      
      if (dateRange !== 'all') {
        const now = new Date();
        endDate = endOfDay(now).toISOString();
        switch (dateRange) {
          case '7d':
            startDate = startOfDay(subDays(now, 7)).toISOString();
            break;
          case '30d':
            startDate = startOfDay(subDays(now, 30)).toISOString();
            break;
          case '90d':
            startDate = startOfDay(subDays(now, 90)).toISOString();
            break;
        }
      }

      // Per la modalita' "scheda persona" (type='person', id=person_id) non
      // esiste un singolo customer/company anchor: chiamiamo solo l'endpoint
      // interactions person-first e lasciamo gli altri risorse a null/[].
      const isPersonView = target.type === 'person';
      const contactEndpoint = target.type === 'b2b'
        ? `/crm/companies/${target.id}`
        : target.type === 'b2c'
          ? `/crm/customers/${target.id}`
          : null;

      const interactionsUrl = isPersonView
        ? `/crm/interactions/person/${target.id}`
        : `/crm/interactions/${target.type}/${target.id}`;

      type InteractionsResponse = {
        interactions?: Interaction[];
        linkedLeads?: LinkedLead[];
      };
      type CampaignsResponse = { campaigns?: Campaign[] };

      const [interactionsRes, statsRes, campaignsRes, contactRes] = await Promise.all([
        apiClient.get<InteractionsResponse>(interactionsUrl),
        isPersonView
          ? Promise.resolve<InteractionStats | null>(null)
          : apiClient
              .get<InteractionStats>(`/crm/interactions/${target.type}/${target.id}/stats${startDate ? `?startDate=${startDate}&endDate=${endDate}` : ''}`)
              .catch(() => null),
        isPersonView
          ? Promise.resolve<CampaignsResponse>({ campaigns: [] })
          : apiClient
              .get<CampaignsResponse>(`/crm/contacts/${target.type}/${target.id}/campaigns`)
              .catch(() => ({ campaigns: [] as Campaign[] })),
        contactEndpoint
          ? apiClient.get<ContactDetails>(contactEndpoint).catch(() => null)
          : Promise.resolve<ContactDetails | null>(null),
      ]);

      const interactionsData = (interactionsRes as InteractionsResponse) || {};
      setInteractions(Array.isArray(interactionsData.interactions) ? interactionsData.interactions : []);
      setStats(statsRes as InteractionStats | null);
      setCampaigns(Array.isArray((campaignsRes as CampaignsResponse)?.campaigns) ? ((campaignsRes as CampaignsResponse).campaigns ?? []) : []);
      setContactDetails((contactRes as ContactDetails | null) || null);
      setLinkedLeads(Array.isArray(interactionsData.linkedLeads) ? interactionsData.linkedLeads : []);
    } catch (err) {
      console.error('Error loading interactions:', err);
      setError('Errore nel caricamento delle interazioni. Riprova.');
    } finally {
      setLoading(false);
    }
  }, [dateRange, target]);

  useEffect(() => {
    if (open && target) {
      loadData();
    }
  }, [open, target, dateRange, loadData]);

  const loadAppointments = useCallback(async () => {
    if (!target || appointmentsLoaded) return;
    setAppointmentsLoading(true);
    try {
      // Per la scheda persona aggregata gli appuntamenti sono gia' inclusi
      // nelle interactions (filter type='appointment'); per b2c/b2b/lead
      // usiamo l'endpoint dedicato che filtra per customer.
      if (target.type === 'person') {
        type ApptInteraction = Interaction & {
          end_time?: string;
          location?: string;
          meeting_link?: string;
          notes?: string;
          customer_id?: string | null;
          lead_id?: string | null;
          person_id?: string | null;
          type_name?: string;
          type_color?: string;
          user_name?: string;
          assigned_to_name?: string;
          description?: string;
        };
        const source = (interactions as ApptInteraction[]) || [];
        const apptFromInteractions: Appointment[] = source
          .filter((i) => i?.type === 'appointment')
          .map((i) => ({
            id: i.id,
            title: i.title || 'Appuntamento',
            description: i.description,
            start_time: i.timestamp,
            end_time: i.end_time || i.timestamp,
            status: i.status || 'scheduled',
            location: i.location,
            meeting_link: i.meeting_link,
            notes: i.notes,
            service_type_name: i.type_name,
            assigned_to_name: i.assigned_to_name,
          }));
        setAppointments(apptFromInteractions);
        setAppointmentsLoaded(true);
      } else {
        const res = await apiClient.get<{ success: boolean; data: Appointment[] }>(`/calendar/appointments?customer=${target.id}`);
        const data = (res as unknown as { data?: Appointment[] })?.data || [];
        setAppointments(Array.isArray(data) ? data : []);
        setAppointmentsLoaded(true);
      }
    } catch (err) {
      console.error('Error loading appointments:', err);
      setAppointments([]);
      setAppointmentsLoaded(true);
    } finally {
      setAppointmentsLoading(false);
    }
  }, [appointmentsLoaded, interactions, target]);

  const loadEmails = useCallback(async () => {
    if (!target) return;
    setEmailsLoading(true);
    try {
      // Person-first: la scheda persona aggrega le email su tutti i
      // customer/lead collegati al person_id (filtro server-side).
      const isPersonView = target.type === 'person';
      const params = new URLSearchParams();
      if (isPersonView) {
        params.set('person_id', target.id);
      } else {
        params.set('customer_id', target.id);
      }
      const email = contactDetails?.email;
      if (email && !isPersonView) params.set('contact_email', email);
      params.set('limit', '100');
      emailRef.current = email ?? null;
      const res = await apiClient.get<{ success: boolean; data: EmailMessage[] }>(`/email/history?${params.toString()}`);
      const data = (res as unknown as { data?: EmailMessage[] })?.data || [];
      setEmails(Array.isArray(data) ? data : []);
      setEmailsLoaded(true);
    } catch (err) {
      console.error('Error loading emails:', err);
      setEmails([]);
      setEmailsLoaded(true);
    } finally {
      setEmailsLoading(false);
    }
  }, [contactDetails?.email, target]);

  const loadSales = useCallback(async () => {
    if (!target) return;
    setSalesLoading(true);
    try {
      // Person-first: per la scheda persona usa direttamente person_id;
      // altrimenti combina person_id (se noto da contactDetails) e
      // customer_id come fallback.
      const isPersonView = target.type === 'person';
      const params = new URLSearchParams({ limit: '100' });
      if (isPersonView) {
        params.set('person_id', target.id);
      } else {
        if (contactDetails?.person_id) {
          params.set('person_id', contactDetails.person_id);
        }
        params.set('customer_id', target.id);
      }
      type SalesResponse = { data?: { items?: SaleRecord[] }; items?: SaleRecord[] };
      salesPersonIdRef.current = isPersonView ? target.id : contactDetails?.person_id ?? null;
      const res = await apiClient.get<SalesResponse>(`/pos/sales?${params.toString()}`);
      const response = res as SalesResponse;
      const raw = response?.data?.items ?? response?.items ?? [];
      setSales(Array.isArray(raw) ? raw : []);
      setSalesLoaded(true);
    } catch (err) {
      console.error('Error loading sales:', err);
      setSales([]);
      setSalesLoaded(true);
    } finally {
      setSalesLoading(false);
    }
  }, [contactDetails?.person_id, target]);

  const documentsPersonId = target?.type === 'person' ? target.id : contactDetails?.person_id ?? null;

  const loadDocuments = useCallback(async (opts?: { source?: DocumentSource | 'all'; q?: string; offset?: number; append?: boolean }) => {
    if (!target || !documentsPersonId) {
      setDocuments([]);
      setDocumentsTotal(0);
      setDocumentsLoaded(true);
      return;
    }
    setDocumentsLoading(true);
    setDocumentsError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(DOCUMENTS_PAGE_SIZE));
      params.set('offset', String(opts?.offset ?? 0));
      const src = opts?.source ?? 'all';
      if (src !== 'all') params.set('source_type', src);
      const q = (opts?.q ?? '').trim();
      if (q) params.set('q', q);
      const raw: unknown = await apiClient.get<PersonDocumentsResponse>(`/crm/persons/${documentsPersonId}/documents?${params.toString()}`);
      const isDocsResponse = (x: unknown): x is PersonDocumentsResponse => {
        if (!x || typeof x !== 'object') return false;
        const candidate = x as { items?: unknown };
        return Array.isArray(candidate.items);
      };
      const body: PersonDocumentsResponse = isDocsResponse(raw)
        ? raw
        : { items: [], total: 0, limit: 0, offset: 0 };
      const list = body.items;
      setDocuments(prev => opts?.append ? [...prev, ...list] : list);
      setDocumentsTotal(typeof body.total === 'number' ? body.total : list.length);
      setDocumentsOffset((opts?.offset ?? 0) + list.length);
      setDocumentsCounts(body.counts_by_source ?? null);
      setDocumentsLoaded(true);
    } catch (err) {
      console.error('Error loading documents:', err);
      setDocuments([]);
      setDocumentsTotal(0);
      setDocumentsError('Errore nel caricamento dei documenti');
      setDocumentsLoaded(true);
    } finally {
      setDocumentsLoading(false);
    }
  }, [documentsPersonId, target]);

  useEffect(() => {
    if (activeTab === 'appuntamenti' && !appointmentsLoaded && target) {
      loadAppointments();
    }
    if (activeTab === 'mail' && !emailsLoaded && target) {
      loadEmails();
    }
    if (activeTab === 'vendite' && !salesLoaded && target) {
      loadSales();
    }
    if (activeTab === 'documenti' && !documentsLoaded && target) {
      loadDocuments();
    }
  }, [activeTab, target, appointmentsLoaded, emailsLoaded, loadAppointments, loadEmails, loadSales, loadDocuments, salesLoaded, documentsLoaded]);

  useEffect(() => {
    if (activeTab === 'documenti' && documentsLoaded && documentsPersonId && target) {
      setDocumentsLoaded(false);
      loadDocuments({ source: documentsSourceFilter, q: documentsSearch, offset: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-esistente senza motivazione, descrizione aggiunta meccanicamente (#3359)
  }, [documentsPersonId, documentsSourceFilter]);

  useEffect(() => {
    if (activeTab !== 'documenti' || !documentsPersonId || !target) return;
    const t = setTimeout(() => {
      loadDocuments({ source: documentsSourceFilter, q: documentsSearch, offset: 0 });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-esistente senza motivazione, descrizione aggiunta meccanicamente (#3359)
  }, [documentsSearch]);

  // Re-fetch SOLO quando person_id arriva (o cambia) dopo il primo load,
  // confrontando il valore reale con quello dell'ultimo fetch: niente toggle
  // di `salesLoaded`, quindi nessun loop con l'effetto primario.
  useEffect(() => {
    if (activeTab !== 'vendite' || !salesLoaded || !target) return;
    const currentPersonId = contactDetails?.person_id ?? null;
    if (currentPersonId && currentPersonId !== salesPersonIdRef.current) {
      loadSales();
    }
  }, [contactDetails?.person_id, activeTab, salesLoaded, target, loadSales]);

  useEffect(() => {
    if (activeTab !== 'mail' || !emailsLoaded || !target) return;
    const currentEmail = contactDetails?.email ?? null;
    if (currentEmail && currentEmail !== emailRef.current) {
      loadEmails();
    }
  }, [contactDetails?.email, activeTab, emailsLoaded, target, loadEmails]);

  useEffect(() => {
    if (!open) {
      setAppointmentsLoaded(false);
      setEmailsLoaded(false);
      setSalesLoaded(false);
      setAppointments([]);
      setEmails([]);
      setSales([]);
      setExpandedEmail(null);
      setDocumentsLoaded(false);
      setDocuments([]);
      setDocumentsError(null);
      setDocumentsSourceFilter('all');
      setDocumentsSearch('');
      setDocumentsTotal(0);
      setDocumentsOffset(0);
      setDocumentsCounts(null);
    }
  }, [open]);

  const getAppointmentStatusBadge = (status: string) => {
    const map: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
      scheduled: { label: 'Programmato', variant: 'default' },
      confirmed: { label: 'Confermato', variant: 'default' },
      completed: { label: 'Completato', variant: 'secondary' },
      cancelled: { label: 'Annullato', variant: 'destructive' },
      no_show: { label: 'Non presentato', variant: 'destructive' },
      rescheduled: { label: 'Riprogrammato', variant: 'outline' },
    };
    const info = map[status] || { label: status, variant: 'outline' as const };
    return <Badge variant={info.variant}>{info.label}</Badge>;
  };

  const groupedInteractions = useMemo(() => {
    const messageGroups: Map<string, Interaction[]> = new Map();
    const callGroups: Map<string, Interaction[]> = new Map();
    const standalone: Interaction[] = [];

    interactions.forEach(interaction => {
      if ((interaction.type === 'whatsapp' || interaction.type === 'sms' || interaction.type === 'telegram') && interaction.conversation_id) {
        const key = `conv-${interaction.conversation_id}`;
        if (!messageGroups.has(key)) {
          messageGroups.set(key, []);
        }
        messageGroups.get(key)!.push(interaction)!;
      } else if (interaction.type === 'call') {
        const callDate = new Date(interaction.timestamp).toISOString().split('T')[0];
        const key = `calls-${callDate}`;
        if (!callGroups.has(key)) {
          callGroups.set(key, []);
        }
        callGroups.get(key)!.push(interaction)!;
      } else {
        standalone.push(interaction);
      }
    });

    const conversationGroups = Array.from(messageGroups.entries()).map(([key, messages]) => {
      const sorted = messages.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      return {
        id: key,
        type: sorted[0]!.type! as 'whatsapp' | 'sms' | 'telegram',
        conversationId: sorted[0]!.conversation_id!,
        messages: sorted,
        lastTimestamp: sorted[0]!.timestamp!,
        firstTimestamp: sorted[sorted.length - 1]!.timestamp!,
        messageCount: sorted.length
      };
    });

    const callDayGroups = Array.from(callGroups.entries()).map(([key, calls]) => {
      const sorted = calls.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      const totalDuration = sorted.reduce((sum, c) => sum + (c.duration || 0), 0);
      const inboundCount = sorted.filter(c => c.direction === 'inbound').length;
      const outboundCount = sorted.filter(c => c.direction === 'outbound').length;
      return {
        id: key,
        type: 'call_group' as const,
        date: key.replace('calls-', ''),
        calls: sorted,
        lastTimestamp: sorted[0]!.timestamp!,
        firstTimestamp: sorted[sorted.length - 1]!.timestamp!,
        callCount: sorted.length,
        totalDuration,
        inboundCount,
        outboundCount
      };
    });

    return { conversationGroups, callDayGroups, standalone };
  }, [interactions]);

  const filteredData = useMemo(() => {
    let filtered = [...groupedInteractions.standalone];
    let filteredConvs = [...groupedInteractions.conversationGroups];
    let filteredCallGroups = [...groupedInteractions.callDayGroups];

    if (channelFilter !== 'all') {
      filtered = filtered.filter(i => i.type === channelFilter);
      filteredConvs = filteredConvs.filter(c => c.type === channelFilter);
      if (channelFilter !== 'call') {
        filteredCallGroups = [];
      }
    }

    // Campaign filter
    if (campaignFilter !== 'all') {
      filtered = filtered.filter(i => i.campaign_id === campaignFilter);
      filteredConvs = filteredConvs.filter(c => 
        c.messages.some(m => m.campaign_id === campaignFilter)
      );
      filteredCallGroups = filteredCallGroups.filter(cg =>
        cg.calls.some(c => c.campaign_id === campaignFilter)
      );
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(i => 
        i.content?.toLowerCase().includes(query) ||
        i.caller_id_number?.includes(query) ||
        i.callee_id_number?.includes(query) ||
        i.form_name?.toLowerCase().includes(query) ||
        i.title?.toLowerCase().includes(query)
      );
      filteredConvs = filteredConvs.filter(c =>
        c.messages.some(m => m.content?.toLowerCase().includes(query))
      );
      filteredCallGroups = filteredCallGroups.filter(cg =>
        cg.calls.some(c => 
          c.caller_id_number?.includes(query) || 
          c.callee_id_number?.includes(query)
        )
      );
    }

    if (dateRange !== 'all') {
      const now = new Date();
      let minDate: Date;
      switch (dateRange) {
        case '7d':
          minDate = subDays(now, 7);
          break;
        case '30d':
          minDate = subDays(now, 30);
          break;
        case '90d':
          minDate = subDays(now, 90);
          break;
        default:
          minDate = new Date(0);
      }
      filtered = filtered.filter(i => new Date(i.timestamp) >= minDate);
      filteredConvs = filteredConvs.filter(c => new Date(c.lastTimestamp) >= minDate);
      filteredCallGroups = filteredCallGroups.filter(cg => new Date(cg.lastTimestamp) >= minDate);
    }

    const combined: Array<Interaction | typeof groupedInteractions.conversationGroups[0] | typeof groupedInteractions.callDayGroups[0]> = [
      ...filtered,
      ...filteredConvs,
      ...filteredCallGroups
    ];
    
    combined.sort((a, b) => {
      const timeA = 'lastTimestamp' in a ? a.lastTimestamp : a.timestamp;
      const timeB = 'lastTimestamp' in b ? b.lastTimestamp : b.timestamp;
      return new Date(timeB).getTime() - new Date(timeA).getTime();
    });

    return combined;
  }, [groupedInteractions, channelFilter, campaignFilter, searchQuery, dateRange]);

  const trendChartData = useMemo(() => {
    if (!stats?.trend.length) return [];
    
    const dateMap = new Map<string, { date: string; call: number; whatsapp: number; telegram: number; sms: number }>();
    
    stats.trend.forEach(item => {
      const dateStr = format(new Date(item.date), 'dd/MM');
      if (!dateMap.has(dateStr)) {
        dateMap.set(dateStr, { date: dateStr, call: 0, whatsapp: 0, telegram: 0, sms: 0 });
      }
      const entry = dateMap.get(dateStr);
      if (item.channel === 'call') entry!.call = Number(item.count);
      else if (item.channel === 'whatsapp') entry!.whatsapp = Number(item.count);
      else if (item.channel === 'telegram') entry!.telegram = Number(item.count);
      else if (item.channel === 'sms') entry!.sms = Number(item.count);
    });

    return Array.from(dateMap.values());
  }, [stats?.trend]);

  const toggleConversation = (id: string) => {
    setExpandedConversations(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getChannelIcon = (type: string) => {
    switch (type) {
      case 'call': return <Phone className="w-4 h-4" />;
      case 'whatsapp': return <MessageSquare className="w-4 h-4" />;
      case 'telegram': return <Send className="w-4 h-4" />;
      case 'sms': return <MessageCircle className="w-4 h-4" />;
      case 'form': return <FileText className="w-4 h-4" />;
      case 'lead': return <TrendingUp className="w-4 h-4" />;
      case 'appointment': return <CalendarDays className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    if (diff < 86400000) {
      return formatDistanceToNow(date, { addSuffix: true, locale: it });
    }
    return format(date, 'dd MMM yyyy HH:mm', { locale: it });
  };

  const pieData = stats?.channelDistribution?.map(item => ({
    name: item.channel === 'call' ? 'Chiamate' : item.channel === 'whatsapp' ? 'WhatsApp' : item.channel === 'telegram' ? 'Telegram' : 'SMS',
    value: item.count,
    color: COLORS[item.channel as keyof typeof COLORS]
  })) || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col dark:bg-gray-900 dark:text-white"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <Users className="w-5 h-5" />
            Scheda Cliente - {target?.name}
            {linkedLeads.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  if (linkedLeads.length === 1) {
                    setOpenLeadId(linkedLeads[0]!.id!);
                    setOpenLeadName(linkedLeads[0]!.title!);
                  } else {
                    setLeadsPickerOpen(true);
                  }
                }}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 hover:bg-orange-200 dark:bg-orange-900/40 dark:text-orange-200 dark:hover:bg-orange-900/60 transition-colors"
                data-testid="chip-linked-leads"
                title="Apri il timeline del lead collegato"
              >
                <Target className="w-3 h-3" />
                {linkedLeads.length} {linkedLeads.length === 1 ? 'lead collegato' : 'lead collegati'}
              </button>
            )}
          </DialogTitle>
          <DialogDescription className="dark:text-gray-400">
            Profilo completo e storico comunicazioni {target?.type === 'b2c' ? 'del cliente' : "dell'azienda"}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mb-3" />
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <Button onClick={loadData} variant="outline">Riprova</Button>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 min-h-0 flex flex-col overflow-hidden">
            <TabsList className="grid w-full grid-cols-6 mb-4">
              <TabsTrigger value="anagrafica" className="flex w-full min-w-0 items-center justify-center gap-2">
                <User className="w-4 h-4 shrink-0" />
                <span className="hidden sm:inline truncate">Anagrafica</span>
              </TabsTrigger>
              <TabsTrigger value="interazioni" className="flex w-full min-w-0 items-center justify-center gap-2">
                <Phone className="w-4 h-4 shrink-0" />
                <span className="hidden sm:inline truncate">Interazioni</span>
              </TabsTrigger>
              <TabsTrigger value="appuntamenti" className="flex w-full min-w-0 items-center justify-center gap-2">
                <CalendarDays className="w-4 h-4 shrink-0" />
                <span className="hidden sm:inline truncate">Appuntamenti</span>
              </TabsTrigger>
              <TabsTrigger value="vendite" className="flex w-full min-w-0 items-center justify-center gap-2">
                <ShoppingCart className="w-4 h-4 shrink-0" />
                <span className="hidden sm:inline truncate">Vendite</span>
              </TabsTrigger>
              <TabsTrigger value="mail" className="flex w-full min-w-0 items-center justify-center gap-2">
                <Mail className="w-4 h-4 shrink-0" />
                <span className="hidden sm:inline truncate">Mail</span>
              </TabsTrigger>
              <TabsTrigger value="documenti" className="flex w-full min-w-0 items-center justify-center gap-2">
                <FolderOpen className="w-4 h-4 shrink-0" />
                <span className="hidden sm:inline truncate">Documenti</span>
              </TabsTrigger>
            </TabsList>

            {/* Tab Anagrafica */}
            <TabsContent value="anagrafica" className="flex-1 min-h-0 overflow-y-auto">
              <div className="p-4 rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                {/* Header con avatar e nome */}
                <div className="flex items-center gap-4 mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                    {target?.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold">{target?.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant={target?.type === 'b2c' ? 'default' : 'secondary'}>
                        {target?.type === 'b2c' ? 'Cliente Privato' : 'Azienda'}
                      </Badge>
                      {contactDetails?.tags?.map((tag, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>
                      ))}
                    </div>
                    {contactDetails?.created_at && (
                      <p className="text-xs text-gray-500 mt-2">
                        Cliente dal {format(new Date(contactDetails.created_at), 'dd MMMM yyyy', { locale: it })}
                      </p>
                    )}
                  </div>
                </div>

                {/* Dati anagrafici in griglia */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Colonna 1 - Contatti */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-sm text-gray-500 uppercase tracking-wide">Contatti</h4>
                    
                    {(contactDetails?.email || contactDetails?.phone || contactDetails?.mobile) ? (
                      <div className="space-y-3">
                        {contactDetails?.email && (
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                              <Mail className="w-4 h-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Email</p>
                              <p className="font-medium">{contactDetails.email}</p>
                            </div>
                          </div>
                        )}
                        {contactDetails?.phone && (
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                              <Phone className="w-4 h-4 text-green-600" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Telefono</p>
                              <p className="font-medium">{contactDetails.phone}</p>
                            </div>
                          </div>
                        )}
                        {contactDetails?.mobile && (
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                              <MessageSquare className="w-4 h-4 text-purple-600" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Cellulare</p>
                              <p className="font-medium">{contactDetails.mobile}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-gray-400 text-sm">Nessun contatto registrato</p>
                    )}
                  </div>

                  {/* Colonna 2 - Indirizzo */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-sm text-gray-500 uppercase tracking-wide">Indirizzo</h4>
                    
                    {(contactDetails?.address || contactDetails?.city) ? (
                      <div className="space-y-2">
                        {contactDetails?.address && (
                          <p className="font-medium">{contactDetails.address}</p>
                        )}
                        {(contactDetails?.postal_code || contactDetails?.city || contactDetails?.province) && (
                          <p className="text-gray-600 dark:text-gray-400">
                            {[contactDetails.postal_code, contactDetails.city, contactDetails.province].filter(Boolean).join(', ')}
                          </p>
                        )}
                        {contactDetails?.country && (
                          <p className="text-gray-500 text-sm">{contactDetails.country}</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-gray-400 text-sm">Nessun indirizzo registrato</p>
                    )}
                  </div>

                  {/* Colonna 3 - Dati fiscali (solo B2B) */}
                  {target?.type === 'b2b' && (
                    <div className="space-y-4">
                      <h4 className="font-semibold text-sm text-gray-500 uppercase tracking-wide">Dati Fiscali</h4>
                      
                      {(contactDetails?.vat_number || contactDetails?.fiscal_code) ? (
                        <div className="space-y-2">
                          {contactDetails?.vat_number && (
                            <div>
                              <p className="text-xs text-gray-500">Partita IVA</p>
                              <p className="font-medium font-mono">{contactDetails.vat_number}</p>
                            </div>
                          )}
                          {contactDetails?.fiscal_code && (
                            <div>
                              <p className="text-xs text-gray-500">Codice Fiscale</p>
                              <p className="font-medium font-mono">{contactDetails.fiscal_code}</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-gray-400 text-sm">Nessun dato fiscale registrato</p>
                      )}
                    </div>
                  )}

                  {/* Colonna 4 - Note */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-sm text-gray-500 uppercase tracking-wide">Note</h4>
                    
                    {contactDetails?.notes ? (
                      <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{contactDetails.notes}</p>
                    ) : (
                      <p className="text-gray-400 text-sm">Nessuna nota</p>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Tab Interazioni - Tutto il contenuto esistente */}
            <TabsContent value="interazioni" className="flex-1 min-h-0 overflow-y-auto space-y-4">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <div className="p-3 rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                <div className="text-xs text-gray-500 mb-1">Totale Interazioni</div>
                <div className="text-2xl font-bold">{stats?.stats?.total || (stats?.totalCalls || 0) + (stats?.totalMessages || 0)}</div>
              </div>
              <div className="p-3 rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-1 text-xs text-blue-600 mb-1">
                  <Phone className="w-3 h-3" /> Chiamate
                </div>
                <div className="text-2xl font-bold">{stats?.stats?.calls || stats?.totalCalls || 0}</div>
              </div>
              <div className="p-3 rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-1 text-xs text-green-600 mb-1">
                  <MessageSquare className="w-3 h-3" /> WhatsApp
                </div>
                <div className="text-2xl font-bold">{stats?.stats?.whatsapp || stats?.totalMessages || 0}</div>
              </div>
              <div className="p-3 rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-1 text-xs mb-1" style={{ color: COLORS.telegram }}>
                  <Send className="w-3 h-3" /> Telegram
                </div>
                <div className="text-2xl font-bold">{stats?.stats?.telegram || 0}</div>
              </div>
              <div className="p-3 rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-1 text-xs text-cyan-600 mb-1">
                  <MessageCircle className="w-3 h-3" /> SMS
                </div>
                <div className="text-2xl font-bold">{stats?.stats?.sms || 0}</div>
              </div>
              <div className="p-3 rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                <div className="text-xs text-gray-500 mb-1">Ultimo Contatto</div>
                <div className="text-sm font-medium">
                  {stats?.lastContact ? (
                    <div className="flex items-center gap-1">
                      {getChannelIcon(stats.lastContact.channel)}
                      <span>{formatTime(stats.lastContact.timestamp)}</span>
                    </div>
                  ) : '-'}
                </div>
              </div>
            </div>

            {/* Charts Row */}
            {(trendChartData.length > 0 || pieData.length > 0) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {trendChartData.length > 0 && (
                  <div className="p-4 rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                    <h4 className="text-sm font-medium mb-3">Trend Ultimi 30 Giorni</h4>
                    <ResponsiveContainer width="100%" height={150}>
                      <LineChart data={trendChartData}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} className="text-gray-500" />
                        <YAxis tick={{ fontSize: 10 }} className="text-gray-500" />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="call" name="Chiamate" stroke={COLORS.call} strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="whatsapp" name="WhatsApp" stroke={COLORS.whatsapp} strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="telegram" name="Telegram" stroke={COLORS.telegram} strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="sms" name="SMS" stroke={COLORS.sms} strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {pieData.length > 0 && (
                  <div className="p-4 rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                    <h4 className="text-sm font-medium mb-3">Distribuzione Canali</h4>
                    <ResponsiveContainer width="100%" height={150}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={60}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}

            {/* Campaigns Section */}
            {campaigns.length > 0 && (
              <div className="rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setCampaignsExpanded(!campaignsExpanded)}
                  className="w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Megaphone className="w-4 h-4 text-purple-500" />
                    <span className="font-medium">Campagne ({campaigns.length})</span>
                  </div>
                  {campaignsExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                
                {campaignsExpanded && (
                  <div className="border-t border-gray-200 dark:border-gray-700 p-3">
                    <div className="flex gap-3 overflow-x-auto pb-2">
                      {campaigns.map(campaign => (
                        <div
                          key={campaign.id}
                          className="flex-shrink-0 w-[280px] p-3 rounded-lg border bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700"
                        >
                          {/* Campaign Header */}
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <div className="font-medium text-sm truncate max-w-[180px]">{campaign.name}</div>
                              <div className="flex items-center gap-1 text-xs text-gray-500">
                                {campaign.type === 'whatsapp' && <MessageSquare className="w-3 h-3 text-green-500" />}
                                {campaign.type === 'sms' && <MessageCircle className="w-3 h-3 text-cyan-500" />}
                                {campaign.type === 'outbound_call' && <Phone className="w-3 h-3 text-blue-500" />}
                                {campaign.type === 'email' && <Mail className="w-3 h-3 text-orange-500" />}
                                <span className="capitalize">{campaign.type.replace('_', ' ')}</span>
                              </div>
                            </div>
                            <Badge variant={
                              campaign.status === 'active' ? 'default' :
                              campaign.status === 'completed' ? 'secondary' :
                              'outline'
                            } className="text-xs">
                              {campaign.status === 'active' ? 'Attiva' :
                               campaign.status === 'completed' ? 'Completata' :
                               campaign.status === 'paused' ? 'In pausa' :
                               campaign.status === 'draft' ? 'Bozza' : 'Annullata'}
                            </Badge>
                          </div>
                          
                          {/* MACRO Stats */}
                          <div className="text-xs text-gray-500 mb-2">
                            <div className="flex items-center gap-1 mb-1">
                              <Users className="w-3 h-3" />
                              <span>{campaign.macro.total} destinatari</span>
                            </div>
                            <div className="flex gap-2">
                              <span className="text-blue-600">{campaign.macro.percentContacted}% contattati</span>
                              <span className="text-green-600">{campaign.macro.percentResponded}% risposte</span>
                            </div>
                          </div>
                          
                          {/* MICRO Stats (this contact) */}
                          <div className="border-t border-gray-200 dark:border-gray-600 pt-2 mt-2">
                            <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Questo contatto:</div>
                            <div className="flex items-center gap-2 text-xs">
                              {campaign.micro.status === 'responded' && <Check className="w-3 h-3 text-green-500" />}
                              {campaign.micro.status === 'contacted' && <Target className="w-3 h-3 text-blue-500" />}
                              {campaign.micro.status === 'converted' && <Check className="w-3 h-3 text-purple-500" />}
                              {campaign.micro.status === 'failed' && <X className="w-3 h-3 text-red-500" />}
                              {campaign.micro.status === 'pending' && <Clock className="w-3 h-3 text-gray-500" />}
                              <span className={
                                campaign.micro.status === 'responded' ? 'text-green-600' :
                                campaign.micro.status === 'contacted' ? 'text-blue-600' :
                                campaign.micro.status === 'converted' ? 'text-purple-600' :
                                campaign.micro.status === 'failed' ? 'text-red-600' : 'text-gray-500'
                              }>
                                {campaign.micro.status === 'responded' ? 'Risposto' :
                                 campaign.micro.status === 'contacted' ? 'Contattato' :
                                 campaign.micro.status === 'converted' ? 'Convertito' :
                                 campaign.micro.status === 'failed' ? 'Fallito' :
                                 campaign.micro.status === 'skipped' ? 'Saltato' : 'In attesa'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                              <span>{campaign.micro.attempts} tentativi</span>
                              <span>{campaign.micro.messageCount} messaggi</span>
                            </div>
                            {campaign.micro.lead && (
                              <div className="flex items-center gap-1 mt-1">
                                <Badge 
                                  variant="secondary" 
                                  className="text-xs"
                                  style={{ backgroundColor: campaign.micro.lead.stageColor + '20', color: campaign.micro.lead.stageColor }}
                                >
                                  {campaign.micro.lead.stageName}
                                </Badge>
                              </div>
                            )}
                          </div>
                          
                          {/* View Messages Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full mt-2 text-xs"
                            onClick={() => setCampaignFilter(campaign.id)}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            Vedi messaggi
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap gap-3 p-3 rounded-lg border bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Cerca contenuto, numero..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>
              </div>
              <Select value={channelFilter} onValueChange={setChannelFilter}>
                <SelectTrigger className="w-[150px] dark:bg-gray-700 dark:border-gray-600">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Canale" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i canali</SelectItem>
                  <SelectItem value="call">Chiamate</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="telegram">Telegram</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                </SelectContent>
              </Select>
              {campaigns.length > 0 && (
                <Select value={campaignFilter} onValueChange={setCampaignFilter}>
                  <SelectTrigger className="w-[180px] dark:bg-gray-700 dark:border-gray-600">
                    <Megaphone className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Campagna" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte le campagne</SelectItem>
                    {campaigns.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="w-[150px] dark:bg-gray-700 dark:border-gray-600">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Periodo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutto</SelectItem>
                  <SelectItem value="7d">Ultimi 7 giorni</SelectItem>
                  <SelectItem value="30d">Ultimi 30 giorni</SelectItem>
                  <SelectItem value="90d">Ultimi 90 giorni</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Interactions List */}
            <div className="space-y-2">
              {filteredData.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Nessuna interazione trovata</p>
                </div>
              ) : (
                filteredData.map((item) => {
                  if ('conversationId' in item) {
                    const isExpanded = expandedConversations.has(item.id);
                    return (
                      <div key={item.id} className="rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                        <button
                          onClick={() => toggleConversation(item.id)}
                          className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                          <div className={`p-2 rounded-full ${item.type === 'whatsapp' ? 'bg-green-100' : item.type === 'telegram' ? 'bg-sky-100' : 'bg-cyan-100'}`}>
                            {item.type === 'whatsapp' ? <MessageSquare className="w-4 h-4 text-green-600" /> : item.type === 'telegram' ? <Send className="w-4 h-4 text-sky-600" /> : <MessageCircle className="w-4 h-4 text-cyan-600" />}
                          </div>
                          <div className="flex-1 text-left">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">
                                Conversazione {item.type === 'whatsapp' ? 'WhatsApp' : item.type === 'telegram' ? 'Telegram' : 'SMS'}
                              </span>
                              <Badge variant="secondary" className="text-xs">
                                {item.messageCount} messaggi
                              </Badge>
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatTime(item.lastTimestamp)}
                            </div>
                          </div>
                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                        
                        {isExpanded && (
                          <div className="border-t border-gray-200 dark:border-gray-700">
                            {item.messages.map((msg) => (
                              <div 
                                key={msg.id}
                                className="flex items-start gap-3 p-3 pl-12 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    {msg.direction === 'inbound' ? (
                                      <ArrowDownLeft className="w-3 h-3 text-blue-500" />
                                    ) : (
                                      <ArrowUpRight className="w-3 h-3 text-green-500" />
                                    )}
                                    <span className="text-xs text-gray-500">{formatTime(msg.timestamp)}</span>
                                  </div>
                                  <p className="text-sm mt-1 truncate">{msg.content}</p>
                                </div>
                                <Badge variant={msg.direction === 'inbound' ? 'secondary' : 'outline'} className="shrink-0 text-xs">
                                  {msg.direction === 'inbound' ? 'Entrata' : 'Uscita'}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }

                  // Render call groups
                  if ('type' in item && item.type === 'call_group') {
                    const callGroup = item;
                    const isExpanded = expandedConversations.has(callGroup.id);
                    const formatDuration = (seconds: number) => {
                      const mins = Math.floor(seconds / 60);
                      const secs = seconds % 60;
                      return mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `0:${secs.toString().padStart(2, '0')}`;
                    };
                    return (
                      <div key={callGroup.id} className="rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                        <button
                          onClick={() => toggleConversation(callGroup.id)}
                          className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                          <div className="p-2 rounded-full bg-blue-100">
                            <Phone className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="flex-1 text-left">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-sm">
                                Chiamate del {format(new Date(callGroup.date), 'd MMM yyyy', { locale: it })}
                              </span>
                              <Badge variant="secondary" className="text-xs">
                                {callGroup.callCount} chiamate
                              </Badge>
                              <Badge variant="outline" className="text-xs">
                                {formatDuration(callGroup.totalDuration)} totali
                              </Badge>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                              <span className="flex items-center gap-1">
                                <ArrowDownLeft className="w-3 h-3 text-blue-500" />
                                {callGroup.inboundCount} in entrata
                              </span>
                              <span className="flex items-center gap-1">
                                <ArrowUpRight className="w-3 h-3 text-green-500" />
                                {callGroup.outboundCount} in uscita
                              </span>
                            </div>
                          </div>
                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                        
                        {isExpanded && (
                          <div className="border-t border-gray-200 dark:border-gray-700">
                            {callGroup.calls.map((call) => (
                              <div 
                                key={call.id}
                                className="flex items-start gap-3 p-3 pl-12 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    {call.direction === 'inbound' ? (
                                      <>
                                        <ArrowDownLeft className="w-3 h-3 text-blue-500" />
                                        <span className="text-sm">Ricevuta da {call.caller_id_number}</span>
                                      </>
                                    ) : (
                                      <>
                                        <ArrowUpRight className="w-3 h-3 text-green-500" />
                                        <span className="text-sm">Verso {call.callee_id_number}</span>
                                      </>
                                    )}
                                    {call.duration !== undefined && call.duration > 0 && (
                                      <span className="text-xs text-gray-500">
                                        - {formatDuration(call.duration)}
                                      </span>
                                    )}
                                    {call.campaign_id && (
                                      <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/20 dark:text-purple-400">
                                        <Megaphone className="w-2 h-2 mr-1" />
                                        {campaigns.find(c => c.id === call.campaign_id)?.name || 'Campagna'}
                                      </Badge>
                                    )}
                                  </div>
                                  <span className="text-xs text-gray-500">{formatTime(call.timestamp)}</span>
                                </div>
                                <Badge variant={call.direction === 'inbound' ? 'secondary' : 'outline'} className="shrink-0 text-xs">
                                  {call.direction === 'inbound' ? 'In entrata' : 'In uscita'}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }

                  const interaction = item;
                  return (
                    <div 
                      key={`${interaction.type}-${interaction.id}`}
                      className="flex items-start gap-3 p-3 rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className={`p-2 rounded-full ${
                        interaction.type === 'call' ? 'bg-blue-100' :
                        interaction.type === 'whatsapp' ? 'bg-green-100' :
                        interaction.type === 'telegram' ? 'bg-sky-100' :
                        interaction.type === 'sms' ? 'bg-cyan-100' :
                        interaction.type === 'form' ? 'bg-purple-100' :
                        interaction.type === 'appointment' ? 'bg-amber-100' :
                        'bg-orange-100'
                      }`}>
                        {getChannelIcon(interaction.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm capitalize">
                              {interaction.type === 'call' ? 'Chiamata' :
                               interaction.type === 'whatsapp' ? 'WhatsApp' :
                               interaction.type === 'telegram' ? 'Telegram' :
                               interaction.type === 'sms' ? 'SMS' :
                               interaction.type === 'form' ? 'Form' :
                               interaction.type === 'lead' ? 'Lead' :
                               interaction.type === 'appointment' ? 'Appuntamento' : interaction.type}
                            </span>
                            {interaction.campaign_id && (
                              <TooltipProvider>
                                <TooltipUI>
                                  <TooltipTrigger asChild>
                                    <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-800">
                                      <Megaphone className="w-3 h-3 mr-1" />
                                      {campaigns.find(c => c.id === interaction.campaign_id)?.name || 'Campagna'}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Interazione avvenuta nella campagna: {campaigns.find(c => c.id === interaction.campaign_id)?.name || interaction.campaign_id}</p>
                                  </TooltipContent>
                                </TooltipUI>
                              </TooltipProvider>
                            )}
                          </div>
                          <span className="text-xs text-gray-500">
                            {formatTime(interaction.timestamp)}
                          </span>
                        </div>
                        {interaction.type === 'call' && (
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {interaction.direction === 'inbound' ? 'Ricevuta da' : 'Verso'}{' '}
                            {interaction.caller_id_number || interaction.callee_id_number}
                            {interaction.duration && ` - ${Math.floor(interaction.duration / 60)}:${String(interaction.duration % 60).padStart(2, '0')}`}
                          </div>
                        )}
                        {(interaction.type === 'whatsapp' || interaction.type === 'sms' || interaction.type === 'telegram') && interaction.content && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{interaction.content}</p>
                        )}
                        {interaction.type === 'form' && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Compilato form: {interaction.form_name}
                          </p>
                        )}
                        {interaction.type === 'lead' && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600 dark:text-gray-400">{interaction.title}</span>
                            {interaction.stage_name && (
                              <Badge 
                                variant="secondary" 
                                style={{ backgroundColor: interaction.stage_color + '20', color: interaction.stage_color }}
                              >
                                {interaction.stage_name}
                              </Badge>
                            )}
                          </div>
                        )}
                        {interaction.type === 'appointment' && (
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            <span>{interaction.title || 'Appuntamento'}</span>
                            {interaction.status && (
                              <Badge variant="outline" className="ml-2 text-xs">
                                {interaction.status}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                      {interaction.direction && (
                        <Badge variant={interaction.direction === 'inbound' ? 'secondary' : 'outline'} className="shrink-0">
                          {interaction.direction === 'inbound' ? 'In entrata' : 'In uscita'}
                        </Badge>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            </TabsContent>

            {/* Tab Appuntamenti */}
            <TabsContent value="appuntamenti" className="flex-1 min-h-0 overflow-y-auto">
              <div className="p-4 rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                {appointmentsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-500">Caricamento appuntamenti...</span>
                  </div>
                ) : appointments.length === 0 ? (
                  <div className="text-center text-gray-500 py-12">
                    <CalendarDays className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <h3 className="text-lg font-medium mb-2">Nessun appuntamento</h3>
                    <p>Non ci sono appuntamenti registrati per questo cliente</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {appointments.length} Appuntament{appointments.length === 1 ? 'o' : 'i'}
                      </h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200 dark:border-gray-700">
                            <th className="text-left py-3 px-3 font-medium text-gray-500 dark:text-gray-400">Data</th>
                            <th className="text-left py-3 px-3 font-medium text-gray-500 dark:text-gray-400">Titolo</th>
                            <th className="text-left py-3 px-3 font-medium text-gray-500 dark:text-gray-400">Orario</th>
                            <th className="text-left py-3 px-3 font-medium text-gray-500 dark:text-gray-400">Stato</th>
                            <th className="text-left py-3 px-3 font-medium text-gray-500 dark:text-gray-400">Luogo</th>
                            <th className="text-left py-3 px-3 font-medium text-gray-500 dark:text-gray-400">Note</th>
                          </tr>
                        </thead>
                        <tbody>
                          {appointments
                            .sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime())
                            .map(appt => (
                            <tr key={appt.id} className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                              <td className="py-3 px-3">
                                <div className="font-medium text-gray-900 dark:text-white">
                                  {format(new Date(appt.start_time), 'dd MMM yyyy', { locale: it })}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {formatDistanceToNow(new Date(appt.start_time), { addSuffix: true, locale: it })}
                                </div>
                              </td>
                              <td className="py-3 px-3">
                                <div className="font-medium text-gray-900 dark:text-white">{appt.title}</div>
                                {appt.description && (
                                  <div className="text-xs text-gray-500 mt-1 line-clamp-2">{appt.description}</div>
                                )}
                              </td>
                              <td className="py-3 px-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                {format(new Date(appt.start_time), 'HH:mm')} - {format(new Date(appt.end_time), 'HH:mm')}
                              </td>
                              <td className="py-3 px-3">
                                {getAppointmentStatusBadge(appt.status)}
                              </td>
                              <td className="py-3 px-3">
                                {appt.location ? (
                                  <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                                    <MapPin className="w-3 h-3" />
                                    <span className="text-xs">{appt.location}</span>
                                  </div>
                                ) : appt.meeting_link ? (
                                  <a href={appt.meeting_link} target="_blank" rel="noopener noreferrer"
                                    className="flex items-center gap-1 text-blue-600 hover:underline text-xs">
                                    <ExternalLink className="w-3 h-3" />
                                    Link meeting
                                  </a>
                                ) : (
                                  <span className="text-xs text-gray-400">-</span>
                                )}
                              </td>
                              <td className="py-3 px-3">
                                {appt.notes ? (
                                  <span className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">{appt.notes}</span>
                                ) : (
                                  <span className="text-xs text-gray-400">-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Tab Mail */}
            <TabsContent value="mail" className="flex-1 min-h-0 overflow-y-auto">
              <div className="p-4 rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                {emailsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-500">Caricamento email...</span>
                  </div>
                ) : emails.length === 0 ? (
                  <div className="text-center text-gray-500 py-12">
                    <Mail className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <h3 className="text-lg font-medium mb-2">Nessuna email</h3>
                    <p>Non ci sono email registrate per questo cliente</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {emails.length} Email
                      </h3>
                    </div>
                    <div className="space-y-2">
                      {emails
                        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                        .map(email => (
                        <div key={email.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                          <div 
                            className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30"
                            onClick={() => setExpandedEmail(expandedEmail === email.id ? null : email.id)}
                          >
                            <div className={`p-1.5 rounded-full ${
                              email.direction === 'inbound' 
                                ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' 
                                : 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                            }`}>
                              {email.direction === 'inbound' ? <ArrowDown className="w-4 h-4" /> : <ArrowUp className="w-4 h-4" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900 dark:text-white text-sm truncate">
                                  {email.subject || '(Senza oggetto)'}
                                </span>
                                {!email.read_at && email.direction === 'inbound' && (
                                  <Badge variant="default" className="text-xs">Nuovo</Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                                <span>{email.direction === 'inbound' ? 'Da:' : 'A:'} {email.direction === 'inbound' ? email.from_email : email.to_email}</span>
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="text-xs text-gray-500">
                                {format(new Date(email.created_at), 'dd MMM yyyy', { locale: it })}
                              </div>
                              <div className="text-xs text-gray-400">
                                {format(new Date(email.created_at), 'HH:mm')}
                              </div>
                            </div>
                            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expandedEmail === email.id ? 'rotate-180' : ''}`} />
                          </div>
                          {expandedEmail === email.id && (
                            <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-800/50">
                              <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-400 mb-3">
                                <div><span className="font-medium">Da:</span> {email.from_email}</div>
                                <div><span className="font-medium">A:</span> {email.to_email}</div>
                                {email.cc && <div className="col-span-2"><span className="font-medium">CC:</span> {email.cc}</div>}
                                <div><span className="font-medium">Stato:</span> {email.status}</div>
                                <div><span className="font-medium">Data:</span> {format(new Date(email.created_at), 'dd/MM/yyyy HH:mm:ss', { locale: it })}</div>
                              </div>
                              <div className="mt-3 p-3 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700">
                                {email.body_html ? (
                                  <div 
                                    className="text-sm text-gray-700 dark:text-gray-300 prose prose-sm max-w-none dark:prose-invert"
                                    dangerouslySetInnerHTML={{ __html: (email.body_html || '').replace(/src\s*=\s*["']?cid:[^"'\s>]*["']?/gi, 'src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"') }} 
                                  />
                                ) : (
                                  <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-sans">
                                    {email.body_text || '(Nessun contenuto)'}
                                  </pre>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Tab Vendite */}
            <TabsContent value="vendite" className="flex-1 min-h-0 overflow-y-auto">
              <div className="p-4 rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                {salesLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
                    <p className="text-gray-500">Caricamento vendite...</p>
                  </div>
                ) : sales.length === 0 ? (
                  <div className="text-center text-gray-500 py-12">
                    <ShoppingCart className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <h3 className="text-lg font-medium mb-2">Nessuna vendita</h3>
                    <p>Non ci sono vendite registrate per questo cliente</p>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">Storico Vendite</h3>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>{sales.length} {sales.length === 1 ? 'vendita' : 'vendite'}</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          Totale: {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(sales.reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0))}
                        </span>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200 dark:border-gray-700">
                            <th className="text-left py-2 px-3 font-medium text-gray-500">Data</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-500">Operatore</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-500">Prodotti</th>
                            <th className="text-right py-2 px-3 font-medium text-gray-500">Importo Totale</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-500">Tipo Pagamento</th>
                            <th className="text-center py-2 px-3 font-medium text-gray-500"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {sales.map((sale, idx) => (
                            <tr key={sale.id || idx} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900">
                              <td className="py-2 px-3">
                                {(sale.sale_date || sale.created_at) ? format(new Date((sale.sale_date || sale.created_at) as string), 'dd/MM/yyyy HH:mm', { locale: it }) : '-'}
                              </td>
                              <td className="py-2 px-3">{sale.operator_name || '-'}</td>
                              <td className="py-2 px-3 text-xs text-gray-600 dark:text-gray-400 max-w-[220px] truncate" title={sale.items_summary || ''}>
                                {sale.items_summary || `${sale.items_count || 0} articoli`}
                              </td>
                              <td className="py-2 px-3 text-right font-medium">
                                {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(parseFloat(sale.total) || 0)}
                              </td>
                              <td className="py-2 px-3">
                                <Badge variant="outline" className="text-xs">
                                  {sale.payment_method_name || (sale.sale_type === 'credit' ? 'Credito' : sale.sale_type === 'cash' ? 'Contanti' : sale.sale_type || '-')}
                                </Badge>
                              </td>
                              <td className="py-2 px-3 text-center">
                                {sale.id && (
                                  <button
                                    type="button"
                                    onClick={() => window.open(`/pos/sales/${sale.id}`, '_blank')}
                                    className="text-blue-500 hover:text-blue-700 transition-colors"
                                    title="Apri dettaglio vendita"
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Tab Documenti — aggregato per person_id */}
            <TabsContent value="documenti" className="flex-1 min-h-0 overflow-y-auto">
              {renderDocumentsTab({
                documents,
                documentsLoading,
                documentsError,
                documentsPersonId,
                documentsSourceFilter,
                setDocumentsSourceFilter,
                documentsSearch,
                setDocumentsSearch,
                documentsTotal,
                documentsCounts,
                onLoadMore: () => loadDocuments({
                  source: documentsSourceFilter,
                  q: documentsSearch,
                  offset: documentsOffset,
                  append: true,
                }),
              })}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
      {/* Picker dei lead collegati: se ce n'e' piu' di uno, lasciamo
          all'utente la scelta di quale timeline aprire. */}
      <Dialog open={leadsPickerOpen} onOpenChange={setLeadsPickerOpen}>
        <DialogContent className="max-w-md dark:bg-gray-900 dark:text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-4 h-4 text-orange-500" />
              Lead collegati a questa persona
            </DialogTitle>
            <DialogDescription className="dark:text-gray-400">
              Seleziona un lead per aprirne il timeline completo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {linkedLeads.map((l) => (
              <button
                key={l.id}
                type="button"
                onClick={() => {
                  setOpenLeadId(l.id);
                  setOpenLeadName(l.title);
                  setLeadsPickerOpen(false);
                }}
                className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center justify-between gap-2"
                data-testid={`linked-lead-${l.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{l.title}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    Creato il {format(new Date(l.created_at), 'dd MMM yyyy', { locale: it })}
                  </div>
                </div>
                {l.stage_name && (
                  <Badge
                    variant="outline"
                    className="text-xs whitespace-nowrap"
                    style={l.stage_color ? { borderColor: l.stage_color, color: l.stage_color } : undefined}
                  >
                    {l.stage_name}
                  </Badge>
                )}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      {/* Timeline del lead selezionato. */}
      {openLeadId && (
        <LeadTimelineModal
          open={!!openLeadId}
          onOpenChange={(o) => { if (!o) setOpenLeadId(null); }}
          leadId={openLeadId}
          leadName={openLeadName}
        />
      )}
    </Dialog>
  );
}
